import {
  fontFamily,
  fontSize,
  FontSizeProps,
  fontWeight,
  letterSpacing,
  LetterSpacingProps,
  lineHeight,
  LineHeightProps,
  ResponsiveValue,
  textAlign,
  TextAlignProps,
} from 'styled-system';
import { applyTransientProps, PrependDollar } from './styled-system-helpers';

/**
 * font-size
 */
export type StyledFontSizeProps = PrependDollar<FontSizeProps>;

export const styledFontSize = <T extends StyledFontSizeProps>(props: T) => applyTransientProps(props, fontSize);

/**
 * font-weight
 * Custom values to only accept the values we support
 */
export type FontWeightValue = 300 | 400 | 500 | 600 | 700 | 'normal' | 'light' | 'bold';

export type StyledFontWeightProps = {
  $fontWeight?: ResponsiveValue<FontWeightValue>;
};

export const fontWeightMatching: Record<FontWeightValue, number> = {
  300: 300,
  400: 400,
  500: 500,
  600: 600,
  700: 700,
  light: 300,
  normal: 400,
  bold: 700,
};

export function getFontWeight(value: ResponsiveValue<FontWeightValue>): ResponsiveValue<number> {
  if (Array.isArray(value)) {
    return value.map((fw) => fontWeightMatching[fw]);
  }

  return fontWeightMatching[value];
}

export const styledFontWeight = <T extends StyledFontWeightProps>(props: T) =>
  applyTransientProps({ ...props, $fontWeight: getFontWeight(props.$fontWeight) }, fontWeight);

/**
 * font-family
 * Custom values to only accept values we support
 */
export type FontFamilyValue = 'Montserrat' | 'Poppins';

export type StyledFontFamilyProps = {
  $fontFamily?: ResponsiveValue<FontFamilyValue>;
};

export const styledFontFamily = <T extends StyledFontFamilyProps>(props: T) => applyTransientProps(props, fontFamily);

/**
 * letter-spacing
 */
export type StyledLetterSpacingProps = PrependDollar<LetterSpacingProps>;

export const styledLetterSpacing = <T extends StyledLetterSpacingProps>(props: T) =>
  applyTransientProps(props, letterSpacing);

/**
 * line-height
 */
export type StyledLineHeightProps = PrependDollar<LineHeightProps>;

export const styledLineHeight = <T extends StyledLineHeightProps>(props: T) => applyTransientProps(props, lineHeight);

/**
 * text-align
 */
export type StyledTextAlignProps = PrependDollar<TextAlignProps>;

export const styledTextAlign = <T extends StyledTextAlignProps>(props: T) => applyTransientProps(props, textAlign);
